import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useStore } from '../store';
import * as turf from '@turf/turf';
import type { Route, Checkpoint, TrackerUser } from '../types';
import { routeManager } from '../lib/routeManager';
import { mqttClient } from '../lib/mqtt';

const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;

if (!mapboxToken) {
  throw new Error('Mapbox token is required in .env file');
}

mapboxgl.accessToken = mapboxToken;

// 初期位置の設定
const INITIAL_CENTER: [number, number] = [130.73546008345733, 31.744234452821253];
const INITIAL_ZOOM = 19;

// ユーザーごとの色を定義
const USER_COLORS: { [key: string]: string } = {
  'ユーザー1': '#3B82F6', // blue-500
  'ユーザー2': '#10B981'  // emerald-500
};

interface MarkerWithData extends mapboxgl.Marker {
  routeId?: string;
  checkpointId?: string;
}

// カスタムマーカーの作成関数
const createCustomMarker = (type: 'start' | 'checkpoint' | 'goal', color: string, isDragging = false, isEditing = false) => {
  const el = document.createElement('div');
  el.className = 'custom-marker';
  el.style.cursor = isEditing ? 'move' : 'default';
  
  if (type === 'start') {
    el.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${color}" stroke="white" stroke-width="2"/>
        <text x="12" y="16" text-anchor="middle" fill="white" style="font-size: 12px;">S</text>
      </svg>
    `;
  } else if (type === 'goal') {
    el.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${color}" stroke="white" stroke-width="2"/>
        <text x="12" y="16" text-anchor="middle" fill="white" style="font-size: 12px;">G</text>
      </svg>
    `;
  } else {
    el.style.backgroundColor = color;
    el.style.width = '12px';
    el.style.height = '12px';
    el.style.borderRadius = '50%';
    el.style.border = '2px solid white';
    if (isDragging) {
      el.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)';
    }
  }

  return el;
};

// ユーザーマーカーの作成
const createUserMarker = (user: TrackerUser) => {
  const el = document.createElement('div');
  el.className = 'user-marker';
  el.style.position = 'relative';
  el.style.width = '32px';
  el.style.height = '32px';
  
  el.innerHTML = `
    <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      <div class="w-8 h-8 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
           style="background-color: ${USER_COLORS[user.name] || '#3B82F6'}">
        <span class="text-white text-xs font-bold">${user.name.charAt(0)}</span>
      </div>
    </div>
  `;
  return el;
};

interface MapProps {
  showUserLocations?: boolean;
}

interface RouteSources {
  [key: string]: Set<string>;
}

export const Map: React.FC<MapProps> = ({ showUserLocations = false }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const resetPointMarkers = useRef<mapboxgl.Marker[]>([]);
  const userMarkers = useRef<mapboxgl.Marker[]>([]);
  const routeSourcesRef = useRef<RouteSources>({});
  const clickListener = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);
  const { 
    routes, 
    activeRouteId, 
    users, 
    isAddingPoint, 
    pointType, 
    setIsAddingPoint, 
    activePanel,
    activeRoutes,
    getActiveRoutesForUser 
  } = useStore();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // 編集モードかどうかを判定
  const isEditMode = activePanel === 'settings' && !showUserLocations;

  // 地図の初期化
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: INITIAL_CENTER,
        zoom: INITIAL_ZOOM,
        attributionControl: true,
        preserveDrawingBuffer: true
      });

      map.current.on('load', () => {
        console.log('Map loaded');
        setMapLoaded(true);
      });

      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
      });

    } catch (error) {
      console.error('Error initializing map:', error);
    }

    return () => {
      markers.current.forEach(marker => marker.remove());
      resetPointMarkers.current.forEach(marker => marker.remove());
      userMarkers.current.forEach(marker => marker.remove());
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // ルートの更新関数
  const updateRoute = React.useCallback((route: Route, layerId: string, userId?: string) => {
    if (!map.current || !mapLoaded) return;

    try {
      const sortedCheckpoints = [...route.checkpoints].sort((a, b) => {
        if (a.type === 'start') return -1;
        if (b.type === 'start') return 1;
        if (a.type === 'goal') return 1;
        if (b.type === 'goal') return -1;
        return 0;
      });

      if (sortedCheckpoints.length < 2) return;

      const coordinates = sortedCheckpoints.map(cp => cp.position);
      
      const user = userId ? users.find(u => u.id === userId) : undefined;
      const userActiveRoutes = userId ? getActiveRoutesForUser(userId) : undefined;
      const isActive = userActiveRoutes?.has(route.id) ?? false;
      const isCompleted = user?.completedRoutes?.includes(route.id) ?? false;
      const routeColor = isActive ? (isCompleted ? '#9CA3AF' : route.color) : '#9CA3AF';

      const sourceId = `${layerId}-source`;
      const lineId = `${layerId}-line`;

      // ソースが存在する場合は更新、存在しない場合は作成
      if (map.current.getSource(sourceId)) {
        (map.current.getSource(sourceId) as mapboxgl.GeoJSONSource).setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates
          }
        });

        requestAnimationFrame(() => {
          if (map.current?.getLayer(lineId)) {
            map.current.setPaintProperty(lineId, 'line-color', routeColor);
          }
        });
      } else {
        // 新しいソースとレイヤーを作成
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates
            }
          }
        });

        map.current.addLayer({
          id: lineId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': routeColor,
            'line-width': route.id === activeRouteId ? 5 : 3,
            'line-opacity': 0.8
          }
        });

        // レイヤーIDを追跡
        const userKey = userId || 'default';
        if (!routeSourcesRef.current[userKey]) {
          routeSourcesRef.current[userKey] = new Set();
        }
        routeSourcesRef.current[userKey].add(lineId);
      }
    } catch (error) {
      console.error('Error updating route:', error);
    }
  }, [activeRouteId, users, getActiveRoutesForUser, mapLoaded]);

  // MQTTクライアントの接続
  useEffect(() => {
    if (showUserLocations) {
      mqttClient.connect();
      return () => {
        mqttClient.disconnect();
      };
    }
  }, [showUserLocations]);

  // ユーザーマーカーの更新
  useEffect(() => {
    if (!map.current || !mapLoaded || !showUserLocations) return;

    userMarkers.current.forEach(marker => marker.remove());
    userMarkers.current = [];

    users.forEach(user => {
      if (!user.currentPosition) return;

      const el = createUserMarker(user);
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
        offset: [0, -16],
        rotationAlignment: 'map',
        pitchAlignment: 'map'
      })
        .setLngLat(user.currentPosition)
        .addTo(map.current!);

      if (user.lastRouteStatus?.bearing !== undefined) {
        marker.setRotation(user.lastRouteStatus.bearing);
      }
      
      userMarkers.current.push(marker);
    });
  }, [users, mapLoaded, showUserLocations]);

  // クリックリスナーの設定
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (clickListener.current) {
      map.current.off('click', clickListener.current);
      clickListener.current = null;
    }

    if (isAddingPoint && activeRouteId && isEditMode) {
      clickListener.current = async (e: mapboxgl.MapMouseEvent) => {
        if (isProcessing) return;

        e.preventDefault();
        setIsProcessing(true);

        try {
          const point: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          const route = routes.find(r => r.id === activeRouteId);
          
          if (route) {
            const newCheckpoint: Checkpoint = {
              id: `checkpoint-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name: pointType === 'start' ? 'スタート' : 
                    pointType === 'goal' ? 'ゴール' : 
                    `チェックポイント${route.checkpoints.length + 1}`,
              position: point,
              type: pointType
            };

            await routeManager.addCheckpoint(activeRouteId, newCheckpoint);

            if (pointType === 'goal') {
              setIsAddingPoint(false);
            }
          }
        } catch (error) {
          console.error('Error adding checkpoint:', error);
          alert('チェックポイントの追加に失敗しました');
        }
        
        setIsProcessing(false);
      };

      map.current.on('click', clickListener.current);
      map.current.getCanvas().style.cursor = 'crosshair';
    } else if (map.current) {
      map.current.getCanvas().style.cursor = 'grab';
    }
  }, [isAddingPoint, activeRouteId, pointType, routes, isProcessing, setIsAddingPoint, isEditMode]);

  // ルートとマーカーの描画
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    try {
      // マーカーのクリーンアップ
      markers.current.forEach(marker => marker.remove());
      resetPointMarkers.current.forEach(marker => marker.remove());
      markers.current = [];
      resetPointMarkers.current = [];

      // 現在のルートIDを収集
      const currentRouteIds: { [key: string]: Set<string> } = {};
      routes.forEach(route => {
        users.forEach(user => {
          const userId = user.id;
          if (!currentRouteIds[userId]) {
            currentRouteIds[userId] = new Set();
          }
          currentRouteIds[userId].add(`route-${route.id}-${userId}-line`);
        });
      });

      // 不要なレイヤーとソースを削除
      Object.entries(routeSourcesRef.current).forEach(([userId, userLayers]) => {
        const currentUserLayers = currentRouteIds[userId] || new Set();
        userLayers.forEach(layerId => {
          if (!currentUserLayers.has(layerId) && map.current) {
            if (map.current.getLayer(layerId)) {
              map.current.removeLayer(layerId);
            }
            const sourceId = layerId.replace('-line', '-source');
            if (map.current.getSource(sourceId)) {
              map.current.removeSource(sourceId);
            }
            userLayers.delete(layerId);
          }
        });
        if (userLayers.size === 0) {
          delete routeSourcesRef.current[userId];
        }
      });

      // ルートの描画
      routes.forEach(route => {
        const isEditing = route.id === activeRouteId && isEditMode;

        // チェックポイントのマーカー追加
        route.checkpoints.forEach(checkpoint => {
          const selectedUser = users[0];
          const userActiveRoutes = getActiveRoutesForUser(selectedUser.id);
          const isActive = userActiveRoutes.has(route.id);
          const isCompleted = selectedUser.completedRoutes?.includes(route.id);
          const markerColor = isActive ? (isCompleted ? '#9CA3AF' : route.color) : '#9CA3AF';

          const el = createCustomMarker(checkpoint.type, markerColor, false, isEditing);
          const marker = new mapboxgl.Marker({
            element: el,
            draggable: isEditing
          })
            .setLngLat(checkpoint.position)
            .addTo(map.current!) as MarkerWithData;

          marker.routeId = route.id;
          marker.checkpointId = checkpoint.id;

          if (isEditing) {
            marker.on('dragstart', () => {
              setIsDragging(true);
              const element = marker.getElement();
              element.style.zIndex = '1000';
              if (checkpoint.type === 'checkpoint') {
                element.firstChild?.setAttribute('style', `
                  background-color: ${route.color};
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                  border: 2px solid white;
                  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
                `);
              }
            });

            marker.on('drag', () => {
              const position = marker.getLngLat();
              const updatedRoute = {
                ...route,
                checkpoints: route.checkpoints.map(cp =>
                  cp.id === checkpoint.id
                    ? { ...cp, position: [position.lng, position.lat] }
                    : cp
                )
              };
              users.forEach(user => {
                const layerId = `route-${route.id}-${user.id}`;
                updateRoute(updatedRoute, layerId, user.id);
              });
            });

            marker.on('dragend', async () => {
              setIsDragging(false);
              const element = marker.getElement();
              element.style.zIndex = '';
              if (checkpoint.type === 'checkpoint') {
                element.firstChild?.setAttribute('style', `
                  background-color: ${route.color};
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                  border: 2px solid white;
                `);
              }

              const position = marker.getLngLat();
              const updatedCheckpoint = {
                ...checkpoint,
                position: [position.lng, position.lat] as [number, number]
              };

              try {
                await routeManager.updateCheckpoint(route.id, updatedCheckpoint);
              } catch (error) {
                console.error('Error updating checkpoint position:', error);
                alert('チェックポイントの位置の更新に失敗しました');
                marker.setLngLat(checkpoint.position);
                users.forEach(user => {
                  const layerId = `route-${route.id}-${user.id}`;
                  updateRoute(route, layerId, user.id);
                });
              }
            });
          }

          markers.current.push(marker);
        });

        // ルートラインの描画
        if (route.checkpoints.length >= 2) {
          users.forEach(user => {
            const layerId = `route-${route.id}-${user.id}`;
            updateRoute(route, layerId, user.id);
          });
        }
      });
    } catch (error) {
      console.error('Error rendering routes and markers:', error);
    }
  }, [routes, activeRouteId, mapLoaded, users, updateRoute, showUserLocations, isEditMode, getActiveRoutesForUser]);

  return (
    <div ref={mapContainer} className="w-full h-full bg-gray-100" />
  );
};